/**
 * offlineStore.ts
 * Kapselt IndexedDB für den Offline-Checkout-Mechanismus.
 * Speichert lokal ausgecheckte Aufträge und eine Sync-Queue.
 */

const DB_NAME = 'betriebszentrale-offline';
const DB_VERSION = 1;
const STORE_ORDERS = 'checkedOutOrders';
const STORE_QUEUE  = 'syncQueue';

export interface SyncQueueEntry {
  id: string;           // eindeutige ID des Queue-Eintrags
  orderId: string;      // welcher Auftrag betroffen ist
  type: 'update';       // Erweiterbar für zukünftige Typen
  payload: Partial<any>; // die geänderten Felder
  timestamp: number;    // Unix-Timestamp der Änderung
}

// ─── DB initialisieren ──────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_ORDERS)) {
        db.createObjectStore(STORE_ORDERS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        db.createObjectStore(STORE_QUEUE, { keyPath: 'id' });
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

// ─── Ausgecheckte Aufträge ───────────────────────────────────────────────────

/** Speichert eine vollständige lokale Kopie eines Auftrags */
export async function saveOrderLocally(order: any): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ORDERS, 'readwrite');
    tx.objectStore(STORE_ORDERS).put({ ...order, _savedAt: Date.now() });
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

/** Lädt eine lokale Kopie eines Auftrags */
export async function getLocalOrder(orderId: string): Promise<any | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_ORDERS, 'readonly')
                  .objectStore(STORE_ORDERS)
                  .get(orderId);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror   = () => reject(req.error);
  });
}

/** Gibt alle lokal gespeicherten Aufträge zurück */
export async function getAllLocalOrders(): Promise<any[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_ORDERS, 'readonly')
                  .objectStore(STORE_ORDERS)
                  .getAll();
    req.onsuccess = () => resolve(req.result ?? []);
    req.onerror   = () => reject(req.error);
  });
}

/** Löscht den lokalen Eintrag nach erfolgreichem Sync */
export async function clearLocalOrder(orderId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_ORDERS, 'readwrite');
    tx.objectStore(STORE_ORDERS).delete(orderId);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

// ─── Sync-Queue ──────────────────────────────────────────────────────────────

/** Fügt eine Änderung zur Sync-Queue hinzu */
export async function addToSyncQueue(entry: Omit<SyncQueueEntry, 'id' | 'timestamp'>): Promise<void> {
  const db = await openDB();
  const fullEntry: SyncQueueEntry = {
    ...entry,
    id: `sq-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    timestamp: Date.now(),
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, 'readwrite');
    tx.objectStore(STORE_QUEUE).put(fullEntry);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

/** Gibt alle ausstehenden Einträge der Sync-Queue zurück */
export async function getSyncQueue(): Promise<SyncQueueEntry[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_QUEUE, 'readonly')
                  .objectStore(STORE_QUEUE)
                  .getAll();
    req.onsuccess = () => resolve(req.result ?? []);
    req.onerror   = () => reject(req.error);
  });
}

/** Löscht einen einzelnen Queue-Eintrag nach erfolgreichem Sync */
export async function removeFromSyncQueue(entryId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, 'readwrite');
    tx.objectStore(STORE_QUEUE).delete(entryId);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

/** Löscht die gesamte Sync-Queue (nach vollständigem Sync) */
export async function clearSyncQueue(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, 'readwrite');
    tx.objectStore(STORE_QUEUE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}
